import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Users, CloudSun, Clock, ShieldPlus, HeartPulse, Flame, Wifi, WifiOff, Play, Gamepad2, ScrollText, Zap, MessageSquare, Image as ImageIcon, Lightbulb, Timer, Link2, ListChecks, BadgeCheck, KeyRound } from "lucide-react";
import api from "@/lib/api";

/* Borderless 1920x1080-ready CEF loading surface (Unreal Web Browser widget).
   Fully owner-driven: theme + custom widget deck + entry-exam join gate.
   Game bridge: window.ue.gamebridge.joinServer(address) when available. */

function ReactionGame({ accent }) {
  const [state, setState] = useState("idle");
  const [result, setResult] = useState(null);
  const [best, setBest] = useState(() => Number(localStorage.getItem("nc_react_best")) || null);
  const timer = useRef(null);
  const startAt = useRef(0);

  const start = () => {
    setState("waiting"); setResult(null);
    timer.current = setTimeout(() => { startAt.current = performance.now(); setState("go"); }, 1200 + Math.random() * 2600);
  };
  const tap = () => {
    if (state === "waiting") { clearTimeout(timer.current); setState("idle"); setResult("too soon — wait for the flash"); return; }
    if (state === "go") {
      const ms = Math.round(performance.now() - startAt.current);
      setResult(`${ms} ms`);
      if (!best || ms < best) { setBest(ms); localStorage.setItem("nc_react_best", String(ms)); }
      setState("done");
    }
  };
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <div className="flex flex-col gap-2">
      <button data-testid="cef-game-pad" onClick={state === "go" || state === "waiting" ? tap : start}
        className="h-32 border flex flex-col items-center justify-center gap-2 transition-colors select-none"
        style={{ borderColor: `${accent}55`, backgroundColor: state === "go" ? accent : "rgba(0,0,0,0.45)" }}>
        <Zap className="w-5 h-5" style={{ color: state === "go" ? "#000" : accent }} />
        <span className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: state === "go" ? "#000" : "#e4e4e7" }}>
          {state === "waiting" ? "Stand by…" : state === "go" ? "TAP NOW" : state === "done" ? "Again?" : "Reaction Drill"}
        </span>
      </button>
      <div className="flex justify-between font-mono text-[10px] text-zinc-400">
        <span data-testid="cef-game-result">{result || "—"}</span>
        <span>BEST {best ? `${best} ms` : "—"}</span>
      </div>
    </div>
  );
}

function Rotator({ items, accent, label, testid, interval = 6000 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => x + 1), interval);
    return () => clearInterval(t);
  }, [interval]);
  if (!items.length) return null;
  const cur = items[i % items.length];
  return (
    <div data-testid={testid} key={i} className="flex flex-col gap-3 animate-[fadeIn_0.5s_ease]">
      <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: accent }}>{label} {i % items.length + 1} / {items.length}</p>
      <p className="font-sans text-lg leading-snug text-zinc-100">{cur}</p>
      <div className="flex gap-1 mt-1">{items.map((_, j) => <span key={j} className="h-0.5 flex-1" style={{ backgroundColor: j === i % items.length ? accent : "rgba(255,255,255,0.12)" }} />)}</div>
    </div>
  );
}

function Countdown({ at, label, accent }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, new Date(at).getTime() - now);
  const d = Math.floor(diff / 86400000), h = Math.floor(diff / 3600000) % 24, m = Math.floor(diff / 60000) % 60, s = Math.floor(diff / 1000) % 60;
  return (
    <div data-testid="widget-countdown" className="flex flex-col gap-2">
      <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: accent }}>{label || "Countdown"}</p>
      <div className="flex gap-2">
        {[[d, "days"], [h, "hrs"], [m, "min"], [s, "sec"]].map(([v, l]) => (
          <div key={l} className="flex-1 border border-white/10 bg-black/50 py-2.5 text-center">
            <p className="font-sans font-bold text-2xl leading-none">{String(v).padStart(2, "0")}</p>
            <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-500 mt-1">{l}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function GateQuiz({ gate, pid, pname, serverId, accent, onPass }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = gate.questions || [];

  const choose = (ci) => {
    const next = [...answers, ci];
    setAnswers(next);
    if (next.length < qs.length) { setIdx(next.length); return; }
    setBusy(true);
    api.post(`/nc/servers/${serverId}/gate/attempt`, { playercode: Number(pid), name: pname, answers: next })
      .then((r) => { setResult(r.data); if (r.data.passed) onPass(); })
      .catch((e) => setResult({ passed: false, message: e.response?.data?.detail || "Could not grade — retry" }))
      .finally(() => setBusy(false));
  };

  if (!pid) {
    return <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">This server runs an entry exam. When you join from the game client, you'll answer {qs.length} question{qs.length === 1 ? "" : "s"} right here before the city lets you in.</p>;
  }
  if (result) {
    return (
      <div data-testid="gate-result" className="flex flex-col gap-3">
        <p className={`font-sans font-bold text-xl ${result.passed ? "text-emerald-400" : "text-red-400"}`}>{result.passed ? "Exam cleared" : `Not this time — ${result.score ?? 0}%`}</p>
        <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{result.message}</p>
        {!result.passed && (
          <button data-testid="gate-retry" onClick={() => { setIdx(0); setAnswers([]); setResult(null); }}
            className="w-fit font-mono text-[9px] uppercase tracking-[0.25em] border px-4 py-2.5 transition-colors" style={{ borderColor: `${accent}66`, color: accent }}>
            Retake the exam
          </button>
        )}
        {result.passed && <p className="font-mono text-[9px] text-emerald-400/80 uppercase tracking-[0.2em] flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Entry pass issued · valid {result.valid_minutes || 20} min</p>}
      </div>
    );
  }
  const q = qs[idx];
  return (
    <div data-testid="gate-quiz" className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: accent }}>Question {idx + 1} / {qs.length}</p>
        <p className="font-mono text-[8px] text-zinc-500 uppercase">pass mark {gate.pass_min}%</p>
      </div>
      <div className="flex gap-1">{qs.map((_, j) => <span key={j} className="h-0.5 flex-1" style={{ backgroundColor: j < answers.length ? accent : "rgba(255,255,255,0.12)" }} />)}</div>
      <p data-testid="gate-question" className="font-sans text-base leading-snug text-zinc-100">{q.q}</p>
      <div className="flex flex-col gap-1.5">
        {q.choices.map((c, ci) => (
          <button key={ci} data-testid={`gate-choice-${ci}`} disabled={busy} onClick={() => choose(ci)}
            className="text-left border border-white/15 hover:border-white/50 px-3.5 py-2.5 font-mono text-[11px] text-zinc-200 transition-colors disabled:opacity-50">
            <span className="text-zinc-500 mr-2">{String.fromCharCode(65 + ci)}.</span>{c}
          </button>
        ))}
      </div>
    </div>
  );
}

const WIDGET_ICON = { rules: ScrollText, snapshot: Users, queue: Users, game: Gamepad2, text: MessageSquare, image: ImageIcon, discord: Link2, tips: Lightbulb, countdown: Timer };

export default function CefLoadingPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const pid = params.get("pid");
  const pname = params.get("name") || "Citizen";
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [queue, setQueue] = useState(null);
  const [gatePassed, setGatePassed] = useState(false);
  const [verdict, setVerdict] = useState(null); // Steam Guard: {ok, code, message}
  const [verdictChecked, setVerdictChecked] = useState(false);
  const audioRef = useRef(null);
  const [audioOn, setAudioOn] = useState(false);

  const load = useCallback(() => {
    api.get(`/nc/servers/${serverId}/loading`).then((r) => setCfg(r.data)).catch((e) => setErr(e.response?.data?.detail || "Server offline"));
  }, [serverId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Steam Guard — ownership + ban + VPN verdict before anyone touches the join button
  useEffect(() => {
    if (!localStorage.getItem("nc_token")) { setVerdict({ ok: false, code: "steam_required" }); setVerdictChecked(true); return; }
    api.get("/nc/steam/status")
      .then((r) => setVerdict(r.data))
      .catch(() => setVerdict({ ok: false, code: "steam_required" }))
      .finally(() => setVerdictChecked(true));
  }, []);

  // queue keepalive
  useEffect(() => {
    if (!pid || !cfg) return;
    const join = () => api.post(`/nc/servers/${serverId}/queue/join`, { playercode: Number(pid), name: pname }).then((r) => setQueue(r.data)).catch(() => {});
    join();
    const t = setInterval(() => {
      api.get(`/nc/servers/${serverId}/queue/${pid}`).then((r) => setQueue((q) => ({ ...(q || {}), ...r.data }))).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [pid, pname, serverId, cfg]);

  const accent = cfg?.theme?.loading?.accent || cfg?.theme?.accent || "#2E6BFF";
  const loadingFx = cfg?.theme?.loading || {};
  const snap = cfg?.snapshot || {};
  const onDuty = snap.on_duty || {};
  const gate = cfg?.gate || {};
  const gateActive = !!(gate.enabled && (gate.count || 0) > 0);

  const widgets = useMemo(() => {
    if (cfg?.widgets?.length) return cfg.widgets.filter((w) => w.enabled);
    return [{ id: "d-rules", type: "rules", enabled: true, props: {} }, { id: "d-game", type: "game", enabled: true, props: {} }, { id: "d-queue", type: "queue", enabled: true, props: {} }];
  }, [cfg]);

  const joinServer = () => {
    const addr = cfg?.join_address || "";
    try {
      if (window.ue?.gamebridge?.joinserver) window.ue.gamebridge.joinserver(addr);
      else if (window.ue?.gamebridge?.joinServer) window.ue.gamebridge.joinServer(addr);
    } catch { /* browser preview — no bridge */ }
  };

  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (audioOn) { audioRef.current.pause(); setAudioOn(false); }
    else { audioRef.current.play().then(() => setAudioOn(true)).catch(() => {}); }
  };

  const dutyChips = [
    [ShieldPlus, onDuty.police ?? 0, "Police"],
    [HeartPulse, onDuty.ems ?? 0, "EMS"],
    [Flame, onDuty.fire ?? 0, "Fire"],
  ];

  const joinBlocked = (gateActive && pid && !gatePassed) || (verdictChecked && verdict && !verdict.ok);
  const steamLoginUrl = `/api/auth/steam/login?next=${encodeURIComponent(`/cef/loading/${serverId}${pid ? `?pid=${pid}&name=${pname}` : ""}`)}`;

  const renderWidget = (w) => {
    const p = w.props || {};
    switch (w.type) {
      case "rules": return <Rotator items={cfg.theme.rules || []} accent={accent} label="Rule" testid="cef-rule-card" />;
      case "tips": return <Rotator items={(loadingFx.tips?.length ? loadingFx.tips : p.tips) || []} accent={accent} label="Tip" testid="widget-tips" interval={7000} />;
      case "game": return <ReactionGame accent={accent} />;
      case "queue":
        return (
          <div data-testid="cef-queue-panel" className="flex flex-col gap-3">
            {pid ? (
              <>
                <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: accent }}>Queue status — {pname}</p>
                <p className="font-sans font-bold text-5xl">{queue?.in_queue === false ? "—" : `#${queue?.position ?? "…"}`}</p>
                <p className="font-mono text-[10px] text-zinc-400">{queue?.depth ?? snap.queue ?? 0} citizen{(queue?.depth ?? 0) === 1 ? "" : "s"} waiting</p>
                <p className="font-mono text-[9px] text-zinc-400 leading-relaxed">Keep this screen open — your slot is held while you're connected.</p>
              </>
            ) : (
              <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">Joining from the game client drops you into the queue automatically. {snap.queue ? `${snap.queue} currently waiting.` : "No queue right now — straight in."}</p>
            )}
          </div>
        );
      case "snapshot":
        return (
          <div data-testid="widget-snapshot" className="grid grid-cols-3 gap-2">
            {dutyChips.map(([Icon, n, label]) => (
              <div key={label} className="border border-white/10 bg-black/40 p-3 text-center">
                <Icon className="w-4 h-4 mx-auto" style={{ color: accent }} />
                <p className="font-sans font-bold text-lg mt-1 leading-none">{n}</p>
                <p className="font-mono text-[7px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{label} on duty</p>
              </div>
            ))}
          </div>
        );
      case "text":
        return (
          <div data-testid="widget-text" className="flex flex-col gap-2">
            <p className="font-mono text-[9px] uppercase tracking-[0.3em]" style={{ color: accent }}>{p.title}</p>
            <p className="font-sans text-sm leading-relaxed text-zinc-200 whitespace-pre-line">{p.body}</p>
          </div>
        );
      case "image":
        return (
          <figure data-testid="widget-image" className="flex flex-col gap-2">
            <img src={p.url} alt={p.caption || ""} className="w-full max-h-48 object-cover border border-white/10" />
            {p.caption && <figcaption className="font-mono text-[9px] text-zinc-500">{p.caption}</figcaption>}
          </figure>
        );
      case "discord":
        return (
          <div data-testid="widget-discord" className="flex items-center gap-3">
            <Link2 className="w-4 h-4 shrink-0" style={{ color: accent }} />
            <p className="font-mono text-[10px] text-zinc-400 flex-1">Community Discord — whitelists, events, support.</p>
            <a href={p.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-2 text-black shrink-0" style={{ backgroundColor: accent }}>{p.label || "Join"}</a>
          </div>
        );
      case "countdown": return <Countdown at={p.at} label={p.label} accent={accent} />;
      case "socials":
        return (
          <div data-testid="widget-socials" className="flex gap-2 flex-wrap">
            {(p.links || []).map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-2 transition-colors" style={{ borderColor: `${accent}44`, color: accent }}>{l.label}</a>
            ))}
          </div>
        );
      default: return null;
    }
  };

  return (
    <div data-testid="cef-loading" className="fixed inset-0 overflow-hidden bg-black text-zinc-100" style={{ "--accent": accent }}>
      {cfg?.theme?.video_url ? (
        <video data-testid="cef-backdrop-video" className="absolute inset-0 w-full h-full object-cover opacity-45" src={cfg.theme.video_url} autoPlay muted loop playsInline />
      ) : (
        <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 20% 10%, ${accent}22 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, ${accent}18 0%, transparent 50%), #030304` }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/70" />
      {/* loading-screen pack overlays (grain / vignette) */}
      {loadingFx.grain && <div data-testid="loading-grain" className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.9'/%3E%3C/svg%3E\")" }} />}
      {!!loadingFx.vignette && <div data-testid="loading-vignette" className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,${loadingFx.vignette}) 100%)` }} />}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize: "56px 56px" }} />

      {cfg?.theme?.audio?.[0] && <audio ref={audioRef} src={cfg.theme.audio[0]} loop preload="none" />}

      {err && !cfg && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p data-testid="cef-error" className="font-mono text-xs text-red-400 uppercase tracking-[0.3em]">{err}</p>
        </div>
      )}

      {cfg && (
        <div className="relative h-full flex flex-col p-10 lg:p-14">
          {/* header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {cfg.theme.logo_url ? (
                <img src={cfg.theme.logo_url} alt="" className="w-14 h-14 object-contain" />
              ) : (
                <img src="/assets/newcitizen-logo-blue.png" alt="" className="w-14 h-14 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              <div>
                <p className="font-mono text-[10px] tracking-[0.5em] uppercase" style={{ color: accent }}>New Citizen</p>
                <h1 data-testid="cef-server-name" className="font-sans font-bold text-3xl lg:text-4xl uppercase tracking-wide">{cfg.name}</h1>
                <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] mt-1">{cfg.mode} · {cfg.region}{cfg.official ? " · Official" : ""}{gateActive ? " · Entry exam" : ""}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {cfg.theme.audio?.[0] && (
                <button data-testid="cef-audio-toggle" onClick={toggleAudio} className="border px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] flex items-center gap-1.5 transition-colors" style={{ borderColor: `${accent}66`, color: accent }}>
                  <Play className="w-3 h-3" /> {audioOn ? "Mute" : "Music"}
                </button>
              )}
              <span data-testid="cef-online-badge" className={`font-mono text-[9px] uppercase tracking-[0.2em] border px-3 py-2 flex items-center gap-1.5 ${snap.online ? "border-green-500/50 text-green-400" : "border-white/15 text-zinc-500"}`}>
                {snap.online ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />} {snap.online ? "Live" : "Offline"}
              </span>
            </div>
          </div>

          {/* live snapshot strip */}
          <div data-testid="cef-snapshot" className="flex gap-3 mt-8 flex-wrap">
            <div className="border border-white/10 bg-black/50 backdrop-blur px-5 py-3 flex items-center gap-3">
              <Users className="w-4 h-4" style={{ color: accent }} />
              <div><p className="font-sans font-bold text-xl leading-none">{snap.players ?? 0}<span className="text-zinc-500 text-sm">/{snap.max_players ?? 64}</span></p><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-1">Citizens in city</p></div>
            </div>
            {dutyChips.map(([Icon, n, label]) => (
              <div key={label} className="border border-white/10 bg-black/50 backdrop-blur px-5 py-3 flex items-center gap-3">
                <Icon className="w-4 h-4" style={{ color: accent }} />
                <div><p className="font-sans font-bold text-xl leading-none">{n}</p><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-1">{label} on duty</p></div>
              </div>
            ))}
            <div className="border border-white/10 bg-black/50 backdrop-blur px-5 py-3 flex items-center gap-3">
              <CloudSun className="w-4 h-4" style={{ color: accent }} />
              <div><p data-testid="cef-weather" className="font-sans font-bold text-xl leading-none">{snap.weather || "—"}</p><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-1">{snap.map || "Bornwood"}</p></div>
            </div>
            <div className="border border-white/10 bg-black/50 backdrop-blur px-5 py-3 flex items-center gap-3">
              <Clock className="w-4 h-4" style={{ color: accent }} />
              <div><p data-testid="cef-gametime" className="font-sans font-bold text-xl leading-none">{snap.game_time || "—"}</p><p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 mt-1">In-game time</p></div>
            </div>
          </div>

          {/* widget deck + join block */}
          <div className="flex-1 flex items-end mt-8 gap-8 min-h-0">
            <div className="w-[420px] shrink-0 flex flex-col gap-3 max-h-full overflow-y-auto pr-1" style={{ scrollbarWidth: "thin", scrollbarColor: `${accent}44 transparent` }}>
              {gateActive && (
                <div data-testid="gate-panel" className="border p-5 flex flex-col gap-3" style={{ borderColor: gatePassed || !pid ? "rgba(52,211,153,0.4)" : `${accent}66`, backgroundColor: "rgba(0,0,0,0.6)" }}>
                  <p className="font-mono text-[9px] uppercase tracking-[0.3em] flex items-center gap-2" style={{ color: gatePassed ? "#34d399" : accent }}>
                    <ListChecks className="w-3.5 h-3.5" /> Entry exam {gatePassed ? "— passed" : "— required to enter"}
                  </p>
                  {gatePassed && <p className="font-mono text-[10px] text-emerald-400/90 flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Cleared. The city is expecting you.</p>}
                  <GateQuiz gate={gate} pid={pid} pname={pname} serverId={serverId} accent={accent} onPass={() => setGatePassed(true)} />
                </div>
              )}
              {widgets.map((w) => {
                const Icon = WIDGET_ICON[w.type] || MessageSquare;
                return (
                  <div key={w.id} data-testid={`deck-widget-${w.type}`} className="border border-white/10 bg-black/60 backdrop-blur-md p-5">
                    <p className="font-mono text-[8px] uppercase tracking-[0.3em] text-zinc-400 mb-3 flex items-center gap-1.5"><Icon className="w-3 h-3" />{w.type}</p>
                    {renderWidget(w)}
                  </div>
                );
              })}
            </div>

            {/* join block */}
            <div className="ml-auto flex flex-col items-end gap-3 shrink-0 max-w-md">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{snap.map || "Bornwood"} · {cfg.region}</p>

              {/* Steam Guard verdicts */}
              {verdictChecked && verdict && !verdict.ok && verdict.code === "steam_required" && (
                <a data-testid="gate-steam-login" href={steamLoginUrl}
                  className="font-bold uppercase tracking-[0.25em] text-sm px-10 py-5 text-white flex items-center gap-2 transition-transform hover:scale-[1.03]"
                  style={{ backgroundColor: "#1b2838", border: "1px solid #66c0f4", boxShadow: "0 0 40px rgba(102,192,244,0.35)" }}>
                  <KeyRound className="w-4 h-4" /> Sign in with Steam to play
                </a>
              )}
              {verdictChecked && verdict && !verdict.ok && verdict.code === "banned" && (
                <div data-testid="steam-banned" className="border border-red-500/60 bg-red-500/10 px-6 py-5 text-left">
                  <p className="font-bold uppercase tracking-[0.2em] text-red-400 text-sm">Permanently banned</p>
                  <p className="font-mono text-[10px] text-red-300/90 mt-1.5 leading-relaxed">This Steam account is banned from all New Citizen servers by Specpixel Entertainment. <a data-testid="steam-banned-appeal-link" href="/appeal" className="underline text-red-200 hover:text-white transition-colors">Appeal at the Ban Appeal Kiosk</a>.</p>
                </div>
              )}
              {verdictChecked && verdict && !verdict.ok && verdict.code === "not_owned" && (
                <div data-testid="steam-not-owned" className="border border-amber-500/60 bg-amber-500/10 px-6 py-5 text-left">
                  <p className="font-bold uppercase tracking-[0.2em] text-amber-400 text-sm">New Citizen required</p>
                  <p className="font-mono text-[10px] text-amber-200/90 mt-1.5 leading-relaxed">This Steam account doesn't own New Citizen.</p>
                  <a href="https://store.steampowered.com/app/2019700" target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-amber-300 underline underline-offset-4 mt-1 inline-block">Get it on Steam</a>
                </div>
              )}
              {verdictChecked && verdict && !verdict.ok && verdict.code === "vpn_blocked" && (
                <div data-testid="steam-vpn-blocked" className="border border-red-500/60 bg-red-500/10 px-6 py-5 text-left">
                  <p className="font-bold uppercase tracking-[0.2em] text-red-400 text-sm">Connection blocked</p>
                  <p className="font-mono text-[10px] text-red-300/90 mt-1.5 leading-relaxed">VPN and proxy connections aren't allowed — ban-evasion protection.</p>
                </div>
              )}

              {verdictChecked && verdict?.ok && (
                <p data-testid="steam-verified" className="font-mono text-[9px] uppercase tracking-[0.25em] text-emerald-400 flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5" /> Steam verified{verdict.owns_verified ? " · owns New Citizen" : ""}
                </p>
              )}
              {!verdictChecked && <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] animate-pulse">Verifying Steam…</p>}

              <button data-testid="cef-join-btn" onClick={joinServer} disabled={joinBlocked}
                className="font-sans font-bold uppercase tracking-[0.25em] text-lg px-10 py-5 text-black transition-transform enabled:hover:scale-[1.03] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ backgroundColor: accent, boxShadow: joinBlocked ? "none" : `0 0 44px ${accent}55` }}>
                {verdictChecked && verdict && !verdict.ok ? "Steam check failed" : joinBlocked ? "Pass the entry exam" : "Enter the City"}
              </button>
              <p className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.2em]">specpixel.com · protected by nc-shield</p>
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  );
}
